import React from "react";
import { Youtube } from "lucide-react";

/**
 * Extract the YouTube video id from any supported URL shape:
 *   - youtu.be/ID
 *   - youtube.com/watch?v=ID
 *   - youtube.com/embed/ID
 *   - youtube.com/shorts/ID
 */
export function extractYouTubeId(url) {
  if (!url) return null;
  const s = String(url).trim();
  try {
    const u = new URL(s);
    const h = u.hostname.replace(/^www\./, "");
    if (h === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
    if (h === "youtube.com" || h === "m.youtube.com") {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const m = u.pathname.match(/^\/(?:embed|shorts|v)\/([^/?#]+)/);
      if (m) return m[1];
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Small clickable thumbnail rendered next to a YouTube URL input.
 * Silent (renders nothing) when the URL is empty or invalid.
 */
export default function YouTubeThumbnail({ url, size = 60, "data-testid": testId }) {
  const id = extractYouTubeId(url);
  if (!id) return null;
  const thumb = `https://i.ytimg.com/vi/${id}/mqdefault.jpg`;
  const href = `https://youtu.be/${id}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="relative shrink-0 rounded-md overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow group"
      style={{ width: size * 1.78, height: size }}
      title="Abrir vídeo en YouTube"
      data-testid={testId}
    >
      <img
        src={thumb}
        alt="Vista previa del vídeo de YouTube"
        loading="lazy"
        className="w-full h-full object-cover"
        onError={(e) => {
          // Fallback: hide the image if YouTube blocks hotlink.
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="absolute inset-0 grid place-items-center bg-black/25 group-hover:bg-black/40 transition-colors">
        <div className="w-6 h-6 rounded-full bg-red-600 grid place-items-center shadow-lg">
          <Youtube className="w-3.5 h-3.5 text-white" />
        </div>
      </div>
    </a>
  );
}
